import { reactive, watch, computed, type ComputedRef, isRef } from 'vue';
import { z } from 'zod/v4';

type MaybeComputed<T> = T | ComputedRef<T>;
type AnyZodSchema = z.ZodType;
type ValidationValues = Record<string, unknown>;
type ValidationFieldMeta = {
    key: string;
    kind?: string;
    label?: string;
    required?: boolean;
    requiredWhen?: (values: Record<string, unknown>) => boolean;
    missingWhen?: (values: Record<string, unknown>) => boolean;
    hidden?: boolean;
    visibleWhen?: (values: Record<string, unknown>) => boolean;
};
type UseFormValidationOptions = {
    fieldPaths?: MaybeComputed<string[]>;
    fields?: MaybeComputed<ValidationFieldMeta[]>;
};

const OCCURRED_AT_KEY = 'occurredAt';
export const OCCURRED_AT_FUTURE_MESSAGE =
    "You can't log an incident in the future. Please pick the time that the incident occurred.";

export function isOccurredAtInFuture(value: unknown): boolean {
    let parsed: Date | null = null;

    if (value instanceof Date) {
        parsed = value;
    } else if (typeof value === 'string' && value.trim() !== '') {
        parsed = new Date(value);
    }

    if (!parsed || Number.isNaN(parsed.getTime())) return false;
    return parsed.getTime() > Date.now();
}

const occurredAtNotFutureSchema = z.iso.datetime().refine((value) => {
    return !isOccurredAtInFuture(value);
}, OCCURRED_AT_FUTURE_MESSAGE);

function valuesAreEqual(a: unknown, b: unknown): boolean {
    if (Array.isArray(a) && Array.isArray(b)) {
        return a.length === b.length && a.every((val, i) => val === b[i]);
    }
    return a === b;
}

function unwrap<T>(value: MaybeComputed<T>): T {
    return isRef(value) ? value.value : value;
}

function isIndexable(value: unknown): value is Record<PropertyKey, unknown> {
    return typeof value === 'object' && value !== null;
}

function cloneValue<T>(value: T): T {
    if (Array.isArray(value)) {
        return value.map((entry) => cloneValue(entry)) as T;
    }

    if (value instanceof Date) {
        return new Date(value.getTime()) as T;
    }

    if (value && typeof value === 'object') {
        return Object.fromEntries(
            Object.entries(value as Record<string, unknown>).map(([key, entry]) => [key, cloneValue(entry)]),
        ) as T;
    }

    return value;
}

function syncReactiveRecord(target: Record<string, unknown>, source: Record<string, unknown>) {
    for (const key of Object.keys(target)) {
        if (!(key in source)) {
            delete target[key];
        }
    }

    for (const [key, value] of Object.entries(source)) {
        if (Array.isArray(value) || value instanceof Date || !isIndexable(value)) {
            target[key] = cloneValue(value);
            continue;
        }

        if (!isIndexable(target[key]) || Array.isArray(target[key]) || target[key] instanceof Date) {
            target[key] = {};
        }

        syncReactiveRecord(target[key] as Record<string, unknown>, value);
    }
}

function getValueByPath(root: unknown, path: string): unknown {
    if (!path) {
        return root;
    }

    return path.split('.').reduce<unknown>((acc, key) => {
        if (!isIndexable(acc)) {
            return undefined;
        }
        return acc[key];
    }, root);
}

function getPathKeys(path: readonly PropertyKey[]): { key: string; fallback: string } {
    const segments = path.map((part) => String(part));
    return {
        key: segments.join('.'),
        fallback: segments[0] ?? '',
    };
}

function isFieldVisible(field: ValidationFieldMeta | undefined, values: Record<string, unknown>): boolean {
    if (!field) return true;
    if (field.hidden) return false;
    if (field.visibleWhen) return field.visibleWhen(values);
    return true;
}

function isMissingRequiredValue(field: ValidationFieldMeta, values: Record<string, unknown>): boolean {
    if (field.missingWhen?.(values)) return true;

    const value = getValueByPath(values, field.key);

    if (
        field.kind === 'text' ||
        field.kind === 'textarea' ||
        field.kind === 'datetime' ||
        field.kind === 'select' ||
        field.kind === 'radio'
    ) {
        return typeof value !== 'string' || value.trim() === '';
    }

    if (field.kind === 'multiselect' || field.kind === 'file') {
        return !Array.isArray(value) || value.length === 0;
    }

    if (field.kind === 'number') {
        return value === '' || value === undefined || value === null;
    }

    return value === undefined || value === null || value === '';
}

function normalizeIssueMessage(issue: z.core.$ZodIssue, field?: ValidationFieldMeta) {
    if (issue.code === 'invalid_format' && issue.format === 'datetime' && issue.message === 'Invalid ISO datetime') {
        return 'Please enter a valid date and time.';
    }

    if (issue.code === 'invalid_value' && issue.message.startsWith('Invalid option:')) {
        return field?.required ? 'Please select an option.' : 'Please select a valid option.';
    }

    if (
        issue.code === 'invalid_type' &&
        issue.expected === 'array' &&
        issue.message.startsWith('Invalid input: expected array')
    ) {
        if (field?.kind === 'text' && field.label?.toLowerCase().includes('comma-separated')) {
            return 'Please enter one or more comma-separated values.';
        }
        return field?.required ? 'Please select at least one option.' : 'Please enter a valid list.';
    }

    if (
        issue.code === 'too_small' &&
        issue.origin === 'array' &&
        typeof issue.minimum === 'number' &&
        issue.minimum >= 1 &&
        issue.message.startsWith('Too small: expected array')
    ) {
        return 'Please select at least one option.';
    }

    if (issue.code === 'too_big' && issue.origin === 'string' && typeof issue.maximum === 'number') {
        return `Please use ${issue.maximum} characters or fewer.`;
    }

    return issue.message;
}

// The '' placeholder of an untouched select is not a valid enum value. Optional enums appear
// either as `.nullable()` (invalid_value) or as `z.union([enum, z.null()])` (invalid_union)
// depending on how the OpenAPI spec encodes nullability; in the union case the enum's own
// failure is nested in the union's per-member errors.
function isEnumPlaceholderIssue(issue: z.core.$ZodIssue): boolean {
    if (issue.code === 'invalid_value' && issue.message.startsWith('Invalid option:')) return true;
    if (issue.code !== 'invalid_union') return false;
    return issue.errors.some((memberIssues) => memberIssues.some(isEnumPlaceholderIssue));
}

function shouldIgnoreIssue(issue: z.core.$ZodIssue, field: ValidationFieldMeta | undefined, values: ValidationValues) {
    if (!field) return false;
    if (!isEnumPlaceholderIssue(issue)) return false;

    const isRequired = Boolean(field.required || field.requiredWhen?.(values));
    if (isRequired) return false;

    const value = getValueByPath(values, field.key);
    return value === '' || value === undefined || value === null;
}

function validateOccurredAtNotFuture(
    values: ValidationValues,
    errors: Record<string, string>,
    fieldByKey: Map<string, ValidationFieldMeta>,
): boolean {
    if (!(OCCURRED_AT_KEY in errors)) return false;
    if (errors[OCCURRED_AT_KEY]) return false;

    const field = fieldByKey.get(OCCURRED_AT_KEY);
    if (!isFieldVisible(field, values)) return false;

    const occurredAt = getValueByPath(values, OCCURRED_AT_KEY);
    if (typeof occurredAt !== 'string' || occurredAt.trim() === '') return false;

    const result = occurredAtNotFutureSchema.safeParse(occurredAt);
    if (result.success) return false;

    const issue = result.error.issues[0];
    errors[OCCURRED_AT_KEY] = normalizeIssueMessage(issue, field);
    return true;
}

/**
 * Custom Zod error map that returns 'Required' for empty strings and missing fields,
 * allowing other validations (email, min length, etc.) to use their default messages.
 * This simplifies schema creation, reducing the need for repetitive error messages.
 */
const requiredErrorMap: z.core.$ZodErrorMap = (iss) => {
    const minimum = typeof iss.minimum === 'number' ? iss.minimum : undefined;

    // Only for actually empty strings
    if (iss.code === 'too_small' && minimum === 1 && iss.input === '') {
        return 'Required';
    }
    // Missing required fields
    if (iss.code === 'invalid_type' && iss.input === undefined) {
        return 'Required';
    }
    return undefined;
};

export function useFormValidation<T extends Record<string, unknown>>(
    schema: MaybeComputed<AnyZodSchema>,
    initialValues: T,
    options: UseFormValidationOptions = {},
) {
    const configuredFieldPaths = options.fieldPaths ? unwrap(options.fieldPaths) : undefined;
    const keys = (
        configuredFieldPaths?.length ? configuredFieldPaths : (Object.keys(initialValues) as string[])
    ) as string[];

    const getFieldByKey = () =>
        new Map((options.fields ? unwrap(options.fields) : []).map((field) => [field.key, field]));

    const values = reactive({ ...initialValues }) as T;
    const errors = reactive(Object.fromEntries(keys.map((k) => [k, '']))) as Record<string, string>;

    let validatedSnapshot: Record<string, unknown> | null = null;

    watch(
        () => ({ ...values }),
        (newValues) => {
            if (!validatedSnapshot) return;

            for (const key of keys) {
                if (!errors[key]) continue;
                const prevValue = getValueByPath(validatedSnapshot, key);
                const nextValue = getValueByPath(newValues, key);
                if (!valuesAreEqual(prevValue, nextValue)) {
                    errors[key] = '';
                }
            }
        },
        { deep: true },
    );

    function setErrors(next: Record<string, string>) {
        for (const key of Object.keys(errors)) {
            errors[key] = '';
        }
        for (const [key, message] of Object.entries(next)) {
            errors[key] = message;
        }
    }

    function replaceValues(nextValues: T) {
        syncReactiveRecord(values as Record<string, unknown>, cloneValue(nextValues) as Record<string, unknown>);
        setErrors({});
        validatedSnapshot = null;
    }

    function validate(): boolean {
        const result = unwrap(schema).safeParse(values, { error: requiredErrorMap });
        const fieldByKey = getFieldByKey();

        for (const key of Object.keys(errors)) {
            errors[key] = '';
        }

        let hasVisibleIssues = false;
        if (!result.success) {
            for (const issue of result.error.issues) {
                const { key, fallback } = getPathKeys(issue.path);
                const fieldMeta = fieldByKey.get(key) ?? fieldByKey.get(fallback);
                if (!isFieldVisible(fieldMeta, values)) {
                    continue;
                }
                if (shouldIgnoreIssue(issue, fieldMeta, values)) {
                    continue;
                }
                hasVisibleIssues = true;
                const normalizedMessage = normalizeIssueMessage(issue, fieldMeta);
                if (key && !errors[key]) {
                    errors[key] = normalizedMessage;
                } else if (fallback && !errors[fallback]) {
                    errors[fallback] = normalizedMessage;
                }
            }
        }

        if (validateOccurredAtNotFuture(values, errors, fieldByKey)) {
            hasVisibleIssues = true;
        }

        for (const field of fieldByKey.values()) {
            if (!isFieldVisible(field, values)) continue;
            if (!(field.key in errors)) continue;
            if (errors[field.key]) continue;
            const isRequired = Boolean(field.required || field.requiredWhen?.(values));
            if (!isRequired) continue;
            if (!isMissingRequiredValue(field, values)) continue;

            errors[field.key] = 'Required';
            hasVisibleIssues = true;
        }

        validatedSnapshot = JSON.parse(JSON.stringify(values)) as ValidationValues;
        return !hasVisibleIssues;
    }

    const isValid = computed(() => Object.values(errors).every((e) => e === ''));

    return { values, errors, validate, isValid, setErrors, replaceValues };
}
